import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Reflects the embedded app's reported in-app path in the FinSuite address bar via the URL
 * HASH fragment (e.g. `/app/xpt#/reports/123?tab=summary`), for readable, copy-paste-friendly
 * deep links.
 *
 * The hash is invisible to `BrowserRouter` route matching, so the `/app/:urlName` route and
 * every match site (useEmbeddedApp, EmbedAppPage, …) are unaffected and the iframe is never
 * remounted — only the hash changes.
 *
 * Write-only: it mirrors the CURRENT page. Restoring a user INTO a page from a freshly loaded
 * hash is a separate concern handled later via the parent→child FINSUITE_NAVIGATE_TO command.
 *
 * @param reportedPath - Latest sanitized relative path from the embedded app, or null.
 */
export const useSyncEmbedPathToHash = (reportedPath: string | null): void => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Nothing reported yet (or reset on env/app change): leave the current hash untouched so a
    // load-time deep-link hash is preserved for the future restore flow.
    if (!reportedPath) return;

    const desiredHash = `#${reportedPath}`;
    // Guard: skip when the hash already matches so identical repeated reports cause no churn.
    if (location.hash === desiredHash) return;

    // Reflect the path in the hash only; keep the current pathname + search (e.g. the ?t=
    // reset key). `replace` so in-app navigation doesn't stack browser history entries.
    navigate({ pathname: location.pathname, search: location.search, hash: desiredHash }, { replace: true });
  }, [reportedPath, location.pathname, location.search, location.hash, navigate]);
};
